import { ProposalSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { CustomerTimePickerNotices } from '@components/Common/CustomerTimePickerNotices';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { FlowBackButton } from '@components/Layouts/FlowBackButton';
import { SelectionBottomBar } from '@components/Layouts/SelectionBottomBar';
import { useShowsSelectionBottomBar } from '@components/Layouts/useShowsSelectionBottomBar';
import { ServiceCenterLocation } from '@components/ServiceCenter/ServiceCenterLocation';
import { TZDate } from '@date-fns/tz';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { FieldErrorMessage, LoadingScreen, NoddiButton } from 'noddi-ui-web';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import {
  commonTrackingEvents,
  StationaryTimeSlotPicker,
  tracking,
  useAuthContext,
  useShowSlotAvailability
} from 'noddi-web';
import type { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProposalStationaryGarage } from '../../../hooks/useProposalStationaryGarage';
import { useServiceCenterLocation } from '../../../hooks/useServiceCenterLocation';
import { useStationaryTimeSlotPicker } from '../../../hooks/useStationaryTimeSlotPicker';
import { useTrackProposalStep } from '../../../hooks/useTrackProposalStep';
import {
  useBookingProposalActions,
  useBookingProposalAnalytics,
  useBookingProposalSlug,
  useBookingProposalStationaryReservation
} from '../../../stores/bookingProposalStore';
import {
  getStationaryProposalBookingHorizonDays,
  showsSuggestedProposalTimes
} from '../../../utils/stationaryProposalPolicy';
import {
  getStationaryProposalBackPath,
  getStationaryProposalNextPathAfterTimeChosen
} from '../../../utils/stationaryProposalSteps';
import { getSuggestedDateAnchor } from '../../../utils/suggestedProposalTimes';

/** Same shell as the loaded picker — keeps title/back mounted while data settles. */
const TimeWindowChrome = ({
  slug,
  usesSuggestedTimes,
  children
}: PropsWithChildren<{ slug: string; usesSuggestedTimes: boolean }>) => (
  <ContentWrapperCustomerApp
    className='max-w-screen-md'
    title={t`Choose date and time`}
    backButtonPath={getStationaryProposalBackPath({ step: 'calendar', slug, usesSuggestedTimes })}
  >
    {children}
  </ContentWrapperCustomerApp>
);

export const StationaryTimeWindowPicker = () => {
  const slug = useBookingProposalSlug();
  const analytics = useBookingProposalAnalytics();
  useTrackProposalStep('timePicker');
  const { setStationaryReservation } = useBookingProposalActions();
  const stationaryReservation = useBookingProposalStationaryReservation();
  const showsBottomBar = useShowsSelectionBottomBar(!!stationaryReservation);
  const navigate = useNavigate();
  const showSlotAvailability = useShowSlotAvailability();
  const { isLoggedIn } = useAuthContext();

  const {
    data: proposal,
    isPending: isProposalPending,
    error: proposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });

  const {
    brandDomain,
    garageId,
    primarySalesItemId,
    addonSalesItemIds,
    isLoading: isGaragesLoading,
    isMissingGarage,
    error: garagesError
  } = useProposalStationaryGarage(proposal);
  const usesSuggestedTimes = showsSuggestedProposalTimes(brandDomain);
  // Same open-day rule as the recommended times: a suggested date leads, a Sunday moves to Monday.
  const suggestedDate = getSuggestedDateAnchor(proposal?.dateSuggested);
  const suggestedDateKey = suggestedDate
    ? noddiFormatDate(suggestedDate, CustomDateFormats.NO_DASHED_DATE_ISO_8601_SHORT)
    : undefined;

  const picker = useStationaryTimeSlotPicker({
    garageId,
    salesItemId: primarySalesItemId,
    addonSalesItemIds,
    maxDaysAhead: getStationaryProposalBookingHorizonDays(brandDomain),
    initialDate: suggestedDate ? noddiFormatDate(suggestedDate, NoddiDateFormats.API_DATE) : undefined,
    reservation: stationaryReservation,
    onReservationChange: setStationaryReservation
  });

  // Zoned to the garage like the tiles above it — a plain `new Date` formats in the
  // browser's zone, so a customer abroad would read a different hour, or day, in the
  // bar than on the slot they just tapped.
  const toGarageTime = (value: string) =>
    picker.timeZone ? new TZDate(new Date(value), picker.timeZone) : new Date(value);

  const selectedTimeLabel = stationaryReservation
    ? `${noddiFormatDate(toGarageTime(stationaryReservation.startsAt), CustomDateFormats.FULL_DAY_MONTH)}, ${noddiFormatDate(toGarageTime(stationaryReservation.startsAt), CustomDateFormats.TIME)}–${noddiFormatDate(toGarageTime(stationaryReservation.endsAt), CustomDateFormats.TIME)}`
    : null;

  const resolvedGarageId = proposal?.garage?.id ?? garageId;
  const { name: serviceCenterName, address: serviceCenterAddress } = useServiceCenterLocation({
    garageId: resolvedGarageId,
    serviceOrganizationId: proposal?.serviceDepartment.serviceOrganizationId,
    fallbackName:
      proposal?.garage?.name ?? proposal?.serviceDepartment.userGroup.name ?? proposal?.serviceDepartment.brand.name,
    fallbackAddress: proposal?.garage?.address,
    enabled: !!proposal
  });

  // Keep title / back button mounted while data settles, like the booking flow.
  if (isProposalPending || isGaragesLoading) {
    return (
      <TimeWindowChrome slug={slug} usesSuggestedTimes={usesSuggestedTimes}>
        <LoadingScreen />
      </TimeWindowChrome>
    );
  }

  const pickerError = proposalError ?? garagesError ?? picker.error;
  if (pickerError) {
    return (
      <TimeWindowChrome slug={slug} usesSuggestedTimes={usesSuggestedTimes}>
        <ApiErrorPageWithTrans error={pickerError} />
      </TimeWindowChrome>
    );
  }

  if (!proposal) {
    return null;
  }

  const backPath = getStationaryProposalBackPath({ step: 'calendar', slug, usesSuggestedTimes });

  return (
    <div>
      <ContentWrapperCustomerApp
        className='mb-24 max-w-screen-md'
        title={t`Choose date and time`}
        backButtonPath={backPath}
        hideBackButton={showsBottomBar && !isMissingGarage}
      >
        <div className='flex flex-col gap-5'>
          {serviceCenterAddress && (
            <ServiceCenterLocation name={serviceCenterName} address={serviceCenterAddress} showLabel />
          )}
          <ProposalSpikedTireSeasonBanner />
          <CustomerTimePickerNotices surface='stationary' serviceDepartmentId={proposal?.serviceDepartment.id} />
          {isMissingGarage ? (
            <FieldErrorMessage
              errorMessage={t`Could not find a service center garage for this proposal. Please contact support.`}
            />
          ) : (
            <StationaryTimeSlotPicker
              days={picker.days}
              selectedSlotId={picker.selectedSlotId}
              onSelectSlot={picker.handleSelectSlot}
              onNavigatePrevious={picker.goToPreviousRange}
              onNavigateNext={picker.goToNextRange}
              isPreviousDisabled={picker.isPreviousDisabled}
              isNextDisabled={picker.isNextDisabled}
              dateRangeLabel={picker.dateRangeLabel}
              isPending={picker.isPending}
              pendingSlotId={picker.pendingSlotId}
              showAvailableCount={showSlotAvailability}
              focusDateKey={suggestedDateKey}
              translations={picker.translations}
            />
          )}
        </div>
      </ContentWrapperCustomerApp>

      {!isMissingGarage && (
        <SelectionBottomBar hasSelection={!!stationaryReservation}>
          {/* The bar is on screen from md whether or not anything is picked, so it carries
              the choice as well as the action: an empty white slab said nothing, and by the
              time the customer reached the button the selected slot had scrolled away. */}
          <div className='flex items-center gap-3'>
            <FlowBackButton backPath={backPath} />
            <span className='text-sm max-md:hidden'>
              {selectedTimeLabel ? (
                <>
                  <span className='text-muted-foreground'>
                    <Trans>Selected time</Trans>:{' '}
                  </span>
                  <span className='font-semibold'>{selectedTimeLabel}</span>
                </>
              ) : (
                <span className='text-muted-foreground'>
                  <Trans>Pick a time above to continue</Trans>
                </span>
              )}
            </span>
          </div>
          {/* "Continue", not "Confirm": the booking is confirmed on the summary step. */}
          <NoddiButton
            className='max-md:w-full'
            endIcon='ArrowRight'
            disabled={!stationaryReservation || picker.isReservationPending}
            loading={picker.isReservationPending}
            onClick={() => {
              tracking.track(commonTrackingEvents.proposalTimeSelected, { ...analytics, method: 'picker' });
              navigate(getStationaryProposalNextPathAfterTimeChosen({ slug, isLoggedIn }));
            }}
          >
            <Trans>Continue</Trans>
          </NoddiButton>
        </SelectionBottomBar>
      )}
    </div>
  );
};
